import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  CheckSquare,
  Clock,
  FileText,
  Receipt,
  Briefcase,
  FileDown,
  Settings,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CalendarDays,
  ShieldCheck,
  Crown,
  BarChart3,
  Wallet,
  LineChart,
  GraduationCap,
  PiggyBank,
  Landmark,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/clients", icon: Users, label: "Clients" },
  { path: "/tasks", icon: CheckSquare, label: "All Tasks" },
  { path: "/time", icon: Clock, label: "Time Tracker" },
  { path: "/invoices", icon: Receipt, label: "All Invoices" },
  { path: "/recurring", icon: RefreshCw, label: "Recurring" },
  { path: "/calendar", icon: CalendarDays, label: "Calendar" },
  { path: "/financials", icon: BarChart3, label: "Financials" },
  { path: "/budget", icon: Wallet, label: "Budget Planner" },
  { path: "/analytics", icon: LineChart, label: "Analytics" },
  { path: "/savings", icon: Landmark, label: "Accounts & Balances" },
  { path: "/leads", icon: Briefcase, label: "Leads" },
  { path: "/training", icon: GraduationCap, label: "Training" },
  { path: "/reports", icon: FileDown, label: "Reports" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export const Sidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { subscription, isAdmin } = useAuth();

  // Build nav items dynamically based on user role
  const visibleNavItems = isAdmin
    ? [...navItems, { path: "/admin", icon: ShieldCheck, label: "Admin" }]
    : navItems;

  // On mobile the sidebar slides in as a drawer; on lg+ it's always visible
  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-full bg-card border-r border-border z-40 flex flex-col transition-all duration-300",
        // Desktop: collapsed/expanded width
        collapsed ? "lg:w-16" : "lg:w-64",
        // Mobile: full-width drawer (slide in/out), always w-64
        "w-64",
        // Mobile visibility
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}
      data-testid="sidebar"
    >
      {/* Logo / header row */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {(!collapsed || mobileOpen) && (
          <img
            src={`${process.env.PUBLIC_URL}/app-logo.svg`}
            alt="FreelanceTrackIt"
            className="h-8 w-auto object-contain"
          />
        )}
        {/* Mobile close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="h-8 w-8 lg:hidden flex-shrink-0"
          aria-label="Close menu"
        >
          <X className="h-4 w-4" />
        </Button>
        {/* Desktop collapse toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className={cn("h-8 w-8 hidden lg:flex", collapsed && "mx-auto")}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Subscription Status */}
      {(!collapsed || mobileOpen) && subscription && (
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Crown className={cn(
              "h-4 w-4",
              subscription.active ? "text-yellow-500" : "text-muted-foreground"
            )} />
            <span className="text-xs font-medium">
              {subscription.active ? "Pro Plan" : "No Subscription"}
            </span>
            {subscription.active && (
              <Badge variant="outline" className="text-xs bg-green-500/10 text-green-500 border-green-500/20">
                Active
              </Badge>
            )}
          </div>
          {subscription.paid_until && subscription.active && (
            <p className="text-xs text-muted-foreground mt-1">
              Valid until {new Date(subscription.paid_until).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
            
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={mobileOpen ? onMobileClose : undefined}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {(!collapsed || mobileOpen) && <span>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      {(!collapsed || mobileOpen) && (
        <div className="p-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            © 2026 FreelanceTrackIt
          </p>
        </div>
      )}
    </aside>
  );
};
